import { useEffect, useMemo } from "react"
import { H2 } from "components/ui/typography/titles/Titles"
import { Input, Form } from "antd"
import {
	useDimensionsTolerance,
	useUpdateDimensionsTolerance,
} from "hooks/api/dimensionsTolerance/dimensionsTolerance.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useMessage } from "hooks/message.hook"
import { integerValidator, isPositiveInteger } from "helpers/utils.helper"
import style from "./DimensionsTolerance.module.scss"

const { dimensionsToleranceStyle, title, text, toleranceWrapper, item, input, skeleton, saveButton, wrapper, label } =
	style

export const DimensionsTolerance = () => {
	const {
		data: dimensionsTolerance,
		isPending: dimensionsTolerancePending,
		isRefetching: dimensionsToleranceRefetching,
	} = useDimensionsTolerance()
	const { mutate: updateDimensionsToleranceMutation, isPending: updateDimensionsTolerancePending } =
		useUpdateDimensionsTolerance()
	const { success: showSuccessMessage, error: showErrorMessage } = useMessage()

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	// Update form values when data changes
	useEffect(() => {
		if (dimensionsTolerance) {
			form.setFieldsValue({
				width: dimensionsTolerance.width_tolerance,
				height: dimensionsTolerance.height_tolerance,
				depth: dimensionsTolerance.depth_tolerance,
			})
		}
	}, [dimensionsTolerance, form])

	// Check if form is valid
	const isSaveEnabled = useMemo(() => {
		if (!dimensionsTolerance || !values) return false

		// Check if all fields have valid values
		const hasValidValues =
			values.width !== undefined &&
			values.width !== null &&
			values.width !== "" &&
			values.height !== undefined &&
			values.height !== null &&
			values.height !== "" &&
			values.depth !== undefined &&
			values.depth !== null &&
			values.depth !== ""

		// Check if values are positive integers
		const arePositiveIntegers =
			isPositiveInteger(values.width) && isPositiveInteger(values.height) && isPositiveInteger(values.depth)

		return hasValidValues && arePositiveIntegers
	}, [values, dimensionsTolerance])

	// Save handler for updating dimensions tolerance
	const saveHandle = () => {
		if (!isSaveEnabled) return
		updateDimensionsToleranceMutation(values, {
			onSuccess: () => {
				showSuccessMessage("Dimensions tolerance updated successfully")
			},
			onError: () => {
				showErrorMessage("Failed to update dimensions tolerance")
			},
		})
	}

	return (
		<div className={dimensionsToleranceStyle}>
			<div className={wrapper}>
				<H2 className={title}>Dimensions tolerance</H2>
				<p className={text}>
					The values below define how closely the dimensions of detected objects extracted from architectural drawings
					must match those of corresponding model numbers to be considered a match. The units are inches.
				</p>
				{dimensionsTolerancePending || dimensionsToleranceRefetching ? (
					<div className={skeleton} data-testid="dimensionsToleranceSkeleton">
						<Skeleton rows={2} />
					</div>
				) : (
					<Form form={form}>
						<div className={toleranceWrapper}>
							<div className={item}>
								<span className={label}>Width</span>
								<Form.Item name="width" rules={[{ validator: integerValidator("width") }]}>
									<Input type="number" size="large" placeholder="in" className={input} />
								</Form.Item>
							</div>
							<div className={item}>
								<span className={label}>Height</span>
								<Form.Item
									name="height"
									rules={[{ required: true, message: "Height is required" }, { validator: integerValidator("height") }]}
								>
									<Input type="number" size="large" placeholder="in" className={input} />
								</Form.Item>
							</div>
							<div className={item}>
								<span className={label}>Depth</span>
								<Form.Item name="depth" rules={[{ validator: integerValidator("depth") }]}>
									<Input type="number" size="large" placeholder="in" className={input} />
								</Form.Item>
							</div>
						</div>
					</Form>
				)}
			</div>
			<ButtonPrimary
				onClick={saveHandle}
				className={saveButton}
				submittable={isSaveEnabled && !updateDimensionsTolerancePending}
			>
				Save
			</ButtonPrimary>
		</div>
	)
}
